import { 
    addDays, 
    addMinutes, 
    format, 
    isValid,
    subDays,
    subMinutes
} from 'date-fns';
import { TimeZoneAbbreviations } from '../components/utility/timezones';

/**
 * Subtracts a given number of units from the provided date and
 * returns the updated date.  If an invalid type is supplied
 * a new Date object is returned.
 * @param d The date to manipulate
 * @param value The number of units to subtract
 * @param type The unit type to add
 */
function subtractFromDate(d: Date, value: number, type: string) : Date {

    switch (type.toLowerCase()) {
        case "days":
            return subDays(d, value);
        case "minutes":
            return subMinutes(d, value);
    }

    return new Date();
}

/**
 * Adds a given number of units to the provided date and
 * returns the updated date.  If an invalid type is supplied
 * a new Date object is returned.
 * @param d The date to manipulate
 * @param value The number of units to add
 * @param type The unit type to add
 */
function addToDate(d: Date, value: number, type: string) : Date {

    switch (type.toLowerCase()) {
        case "days":
            return addDays(d, value);
        case "minutes":
            return addMinutes(d, value);
    }

    return new Date();
}

/**
 * Returns a string in MM/DD/YYYY hh:mm:ss a format
 *   e.g. 01/04/2019 03:39:19 pm	
 * @param d The date to format
 */
function toLongDateTime(d?: Date) {
    if (d) {
        return format(d, 'MM/DD/YYYY hh:mm:ss a');
    }

    return '';
}

/**
 * Returns a string in MM/DD/YYYY hh:mm:ss a format
 *   e.g. 01/04/2019 03:39:19 pm	
 * @param d The date to format
 */
function toLongDateTimeShortYear(d?: Date) {
    if (d) {
        return format(d, 'MM/DD/YY hh:mm a');
    }

    return '';
}

/**
 * Returns a string in MM/DD/YYYY hh:mm a format
 *   e.g. 01/04/2019 03:39 pm	
 * @param d The date to format
 */
function toLongDateTimeNoSeconds(d?: Date) {
    if (d) {
        return format(d, 'MM/DD/YYYY hh:mm A');
    }

    return '';
}

/**
 * Returns a string in YYYY-MM-DD format
 *   e.g. 2019-02-18
 * @param d The date to format
 */
function toShortDate(d: Date) {
    if (d) {
        return format(d, 'YYYY-MM-DD');
    }

    return '';
}

/**
 * Returns a string in MM/DD/YYYY format
 *   e.g. 2019-02-18
 * @param d The date to format
 */
function toSlashedShortDate(d: Date) {
    if (d) {
        return format(d, 'MM/DD/YYYY');
    }

    return '';
}

function toLocalDateString(d: Date, tz: string) {
    if (d && tz) {
        return `${ toLongDateTimeNoSeconds(d) } ${ TimeZoneAbbreviations[tz] }`;
    }

    return '';
}

/**
 * Returns a string in YYYYMMDD format
 *   e.g. 20190218
 * @param d The date to format
 */
function toCondensedDate(d: Date) {
    if (d) {
        return format(d, 'YYYYMMDD');
    }

    return '';
}

/**
 * Returns a string in MMMM Do YYYY format
 *  e.g. February 13th 2019
 * @param d The date to format
 */
function toPrettyDate(d: Date) {
    if (d) {
        return format(d, 'MMMM Do YYYY');
    }

    return '';
}

/**
 * Returns a string in YYYYMMDDHHmm format
 *   e.g. 201902180454
 * @param d The date to format
 */
function toCondensedDateTime(d: Date) {
    if (d) {
        return format(d, 'YYYYMMDDHHmm');
    }

    return '';
}

/**
 * Returns a string in YYYYMMDDHHmmss format
 *   e.g. 20190218045454
 * @param d The date to format
 */
function toCondensedDateTimeSeconds(d: Date) {
    if (d) {
        return format(d, 'YYYYMMDDHHmmss');
    }

    return '';
}

/**
 * Returns a string in YYYYMMDDHHmmss format
 *   e.g. 20190218045454
 * @param d The date to format
 */
function toSimpleDateTime(d: Date) {
    if (d) {
        return format(d, 'YYYY-MM-DD HH:mm A');
    }

    return '';
}

function toInputTime(d?: Date) {
    if (d) {

        return format(d, 'HH:mm');

    }

    return ';';
}

/**
 * Uses date-fns to determine if the date is valid.
 * If not valid, or not supplied, false is returned.
 * @param d The possible date object to check
 */
function isValidDate(d?: Date) {
    if (d) {
        return isValid(d);
    }

    return false;    
}

/**
 * NOT FOR NORMAL USE!
 * Generic format function that takes a format string
 * and formats the provided date.
 * 
 * @param d The date to format
 * @param formatStr The format string to format
 */
function _format(d: Date, formatStr: string) {
    if (d) {
        return format(d, formatStr);
    }    

    return '';
}

export { 
    _format,
    addToDate, 
    isValidDate,
    subtractFromDate,
    toCondensedDate,
    toCondensedDateTime,
    toInputTime,
    toLongDateTime,
    toPrettyDate,
    toShortDate,
    toCondensedDateTimeSeconds,
    toSimpleDateTime,
    toLongDateTimeNoSeconds,
    toLocalDateString,
    toSlashedShortDate,
    toLongDateTimeShortYear
};
